import { TextTestID } from "@/constants/TestID";
import type { FAIconName, IconSize } from "@/ui/core/Icon";
import Icon from "@/ui/core/Icon";
import type { TypographyVariant } from "@/ui/core/Text";
import Text from "@/ui/core/Text";
import Box from "@/ui/layout/Box";
import { HStack, VStack } from "@/ui/layout/Stacks";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

type ActionBarSize = "medium" | "small";

type ActionBarVariant = "critical" | "info" | "primary" | "warning";

interface ActionBarProps extends ViewWithTestID {
  align?: "center" | "start";
  iconName: FAIconName;
  message: string;
  size: ActionBarSize;
  title?: string;
  variant: ActionBarVariant;
}

const BackgroundColorMapper: Record<ActionBarVariant, ColorToken> = {
  critical: ColorToken.NotificationCriticalBackground,
  info: ColorToken.Secondary5,
  primary: ColorToken.Primary10,
  warning: ColorToken.TertiaryMarigoldBackground,
};

const IconColorMapper: Record<ActionBarVariant, ColorToken> = {
  critical: ColorToken.NotificationCriticalText,
  info: ColorToken.AccentBlue,
  primary: ColorToken.Primary100,
  warning: ColorToken.TertiaryMarigoldText,
};

const SizeToIconSizeMapper: Record<ActionBarSize, IconSize> = {
  medium: "md",
  small: "sm",
};

const SizeToTextVariant: Record<ActionBarSize, TypographyVariant> = {
  medium: "body",
  small: "caption",
};

const ActionBar = ({
  align = "start",
  iconName,
  message,
  size,
  testID,
  title,
  variant,
}: ActionBarProps) => {
  const backgroundColor = BackgroundColorMapper[variant];
  const iconColor = IconColorMapper[variant];
  const iconSize = SizeToIconSizeMapper[size];
  const textVariant = SizeToTextVariant[size];

  return (
    <Box backgroundColor={backgroundColor} borderRadius={12} width="100%">
      <HStack
        alignItems={title ? "flex-start" : "center"}
        gap={8}
        justifyContent={align === "center" ? "center" : "flex-start"}
        marginBottom={8}
        marginLeft={12}
        marginRight={12}
        marginTop={8}
      >
        <Icon
          color={iconColor}
          name={iconName}
          size={iconSize}
          testID={testID}
        />
        <VStack flexShrink={1} gap={12}>
          {title && (
            <Text
              testID={joinTestIDs(testID, TextTestID.Title)}
              variant="bodyMedium"
            >
              {title}
            </Text>
          )}
          <Text
            flexShrink={1}
            testID={joinTestIDs(testID, TextTestID.Description)}
            variant={textVariant}
          >
            {message}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export type { ActionBarProps };
export default ActionBar;
